import { ContactShadows, Environment, Lightformer, PresentationControls, useGLTF } from '@react-three/drei'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Component, Suspense, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { chapters } from '../data/chapters'
import { getExplodeOffset, interpolateSceneState } from '../lib/sceneState'

const MODEL_PATH = '/models/molika-9bar.glb'
const METAL_NAMES = new Set([
  'high-pressure-boiler',
  'water-cooling-boiler',
  'coil-heat-exchanger',
  'pressure-column',
  'spring-housing',
  'spring-loaded-valve',
  'fin-heat-exchanger',
  'espresso-reservoir',
])
const MOBILE_MACRO_SHOTS = new Set(['pressure', 'thermal', 'valve', 'brew'])

function canUseWebGL() {
  try {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('webgl2')
    if (!context) return false
    context.getExtension('WEBGL_lose_context')?.loseContext()
    return true
  } catch {
    return false
  }
}

class SceneErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error) {
    console.error('Unable to render the MoLiKa 3D scene', error)
  }

  render() {
    if (this.state.failed) return this.props.fallback
    return this.props.children
  }
}

function StaticProduct() {
  return (
    <div className="static-product" role="img" aria-label="MoLiKa 9bar 产品渲染">
      <img src="/images/product-cutaway.webp" alt="" />
    </div>
  )
}

function getModeState(sceneState, mode, interactive) {
  if (!interactive) return sceneState
  if (mode === 'exploded') return { ...sceneState, explode: 1, transparency: 0 }
  if (mode === 'xray') return { ...sceneState, explode: 0.18, transparency: 0.68 }
  return { ...sceneState, explode: 0, transparency: 0 }
}

function FlowPath({ visible, color, reducedMotion }) {
  const particles = useRef([])
  const curve = useMemo(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(0, -0.88, 0.04),
        new THREE.Vector3(0.02, -0.58, 0.03),
        new THREE.Vector3(0.28, -0.36, 0.08),
        new THREE.Vector3(-0.22, -0.15, 0.06),
        new THREE.Vector3(0.04, 0.16, 0.02),
        new THREE.Vector3(0.02, 0.48, 0),
        new THREE.Vector3(0, 0.79, 0),
      ]),
    [],
  )

  useFrame(({ clock }) => {
    particles.current.forEach((particle, index) => {
      if (!particle) return
      const phase = reducedMotion ? index / particles.current.length : clock.elapsedTime * 0.11 + index / 7
      particle.position.copy(curve.getPointAt(phase % 1))
    })
  })

  return (
    <group visible={visible}>
      <mesh>
        <tubeGeometry args={[curve, 96, 0.012, 8, false]} />
        <meshBasicMaterial color={color} transparent opacity={0.36} depthWrite={false} />
      </mesh>
      {Array.from({ length: 7 }, (_, index) => (
        <mesh
          key={index}
          ref={(node) => {
            particles.current[index] = node
          }}
        >
          <sphereGeometry args={[0.024, 12, 12]} />
          <meshBasicMaterial color={color} toneMapped={false} />
        </mesh>
      ))}
    </group>
  )
}

function Model({ state, mode, interactive, reducedMotion, mobile }) {
  const group = useRef()
  const flowGroup = useRef()
  const { scene } = useGLTF(MODEL_PATH)
  const model = useMemo(() => scene.clone(true), [scene])
  const targets = useMemo(() => {
    const result = []
    model.traverse((object) => {
      if (!object.isMesh) return
      object.material = object.material.clone()
      object.material.envMapIntensity = METAL_NAMES.has(object.name) ? 1.45 : 1.05
      object.material.transparent = true
      object.castShadow = true
      object.receiveShadow = true
      result.push({
        object,
        origin: object.position.clone(),
        baseColor: object.material.color.clone(),
      })
    })
    return result
  }, [model])

  useLayoutEffect(
    () => () => {
      targets.forEach(({ object }) => object.material.dispose())
    },
    [targets],
  )

  useFrame((_, delta) => {
    const resolved = getModeState(state, mode, interactive)
    const speed = reducedMotion ? 1 : 1 - Math.exp(-delta * 4.8)
    const positionTarget = new THREE.Vector3(...resolved.modelPosition)
    if (mobile) {
      positionTarget.y -= MOBILE_MACRO_SHOTS.has(state.id)
        ? 1.18
        : state.id === 'system'
          ? 0.5
          : state.id === 'explore'
            ? 0.35
            : 0.18
    }
    const rotationTarget = new THREE.Euler(...resolved.modelRotation)

    group.current.position.lerp(positionTarget, speed)
    group.current.rotation.x = THREE.MathUtils.lerp(group.current.rotation.x, rotationTarget.x, speed)
    group.current.rotation.y = THREE.MathUtils.lerp(group.current.rotation.y, rotationTarget.y, speed)
    group.current.rotation.z = THREE.MathUtils.lerp(group.current.rotation.z, rotationTarget.z, speed)

    targets.forEach(({ object, origin, baseColor }) => {
      const offset = getExplodeOffset(object.name, resolved.explode)
      const target = origin.clone().add(new THREE.Vector3(...offset))
      object.position.lerp(target, speed)

      const focused = !resolved.focus || object.name === resolved.focus
      const targetOpacity = focused ? 1 : Math.max(0.18, 1 - resolved.transparency)
      object.material.opacity = THREE.MathUtils.lerp(object.material.opacity, targetOpacity, speed)
      object.material.depthWrite = object.material.opacity > 0.72
      object.material.color.lerp(
        focused && resolved.focus ? new THREE.Color(resolved.accent) : baseColor,
        speed * (resolved.focus ? 0.28 : 1),
      )
    })

    if (flowGroup.current) {
      flowGroup.current.visible =
        state.id !== 'hero' && state.id !== 'system' && state.id !== 'exploded'
    }
  })

  return (
    <group ref={group} visible={state.hideModel < 0.88}>
      <primitive object={model} dispose={null} />
      <group ref={flowGroup}>
        <FlowPath visible color={state.accent} reducedMotion={reducedMotion} />
      </group>
    </group>
  )
}

function Scene({ story, mode, interactive, reducedMotion }) {
  const cameraTarget = useRef(new THREE.Vector3())
  const { camera, size } = useThree()
  const mobile = size.width < 768
  const sceneState = interpolateSceneState(chapters, story.index, story.progress)

  useFrame((_, delta) => {
    const speed = reducedMotion ? 1 : 1 - Math.exp(-delta * 3.8)
    const target = new THREE.Vector3(...sceneState.target)
    const desiredPosition = new THREE.Vector3(...sceneState.position)
    const mobileDistance = MOBILE_MACRO_SHOTS.has(sceneState.id)
      ? 1.5
      : sceneState.id === 'system'
        ? 1.35
        : 1.25
    const distanceFactor = (mobile ? mobileDistance : 1) *
      (interactive && mode === 'exploded' ? 1.28 : 1)
    desiredPosition.sub(target).multiplyScalar(distanceFactor).add(target)

    camera.position.lerp(desiredPosition, speed)
    cameraTarget.current.lerp(target, speed)
    const desiredFov = mobile ? 38 : 32
    if (Math.abs(camera.fov - desiredFov) > 0.01) {
      camera.fov = THREE.MathUtils.lerp(camera.fov, desiredFov, speed)
      camera.updateProjectionMatrix()
    }
    camera.lookAt(cameraTarget.current)
  })

  return (
    <>
      <ambientLight intensity={0.54} />
      <directionalLight
        position={[4, 5, 3]}
        intensity={3.1}
        color="#fff4dd"
        castShadow={!mobile}
      />
      <directionalLight position={[-3, 1, -4]} intensity={2.2} color="#8ddaf0" />
      <pointLight position={[-2, -2, 2]} intensity={2.6} color={sceneState.accent} distance={7} />
      <Environment resolution={mobile ? 64 : 128}>
        <Lightformer form="rect" intensity={4} position={[0, 4, -3]} scale={[5, 1, 1]} />
        <Lightformer form="ring" intensity={2} position={[-4, 1, 1]} scale={2} />
        <Lightformer form="rect" intensity={3} position={[4, -1, 2]} scale={[2, 4, 1]} />
      </Environment>
      <PresentationControls
        enabled={interactive}
        global={interactive}
        cursor
        speed={1.1}
        zoom={1}
        polar={[-0.32, 0.32]}
        azimuth={[-0.72, 0.72]}
      >
        <Model
          state={sceneState}
          mode={mode}
          interactive={interactive}
          reducedMotion={reducedMotion}
          mobile={mobile}
        />
      </PresentationControls>
      {!mobile && (
        <ContactShadows
          position={[0, -1.08, 0]}
          opacity={0.32}
          scale={4.2}
          blur={2.8}
          far={2.1}
          frames={1}
          resolution={512}
        />
      )}
    </>
  )
}

export default function ProductCanvas({ story, mode, interactive, reducedMotion }) {
  const supported = useMemo(canUseWebGL, [])
  const [contextLost, setContextLost] = useState(false)
  const fallback = <StaticProduct />

  if (!supported || contextLost) return fallback

  return (
    <SceneErrorBoundary fallback={fallback}>
      <div
        className={`product-canvas ${
          chapters[story.index]?.hideModel ? 'product-canvas--muted' : ''
        } ${interactive ? 'product-canvas--interactive' : ''}`}
      >
        <Canvas
          camera={{
            position: chapters[0].position,
            fov: window.innerWidth < 768 ? 38 : 32,
            near: 0.1,
            far: 50,
          }}
          dpr={window.innerWidth < 768 ? 1 : [1, 1.5]}
          gl={{
            alpha: true,
            antialias: window.innerWidth >= 768,
            powerPreference: 'high-performance',
          }}
          shadows={window.innerWidth >= 768 ? { type: THREE.PCFShadowMap } : false}
          onCreated={({ gl }) => {
            gl.setClearColor(0x000000, 0)
            gl.domElement.addEventListener(
              'webglcontextlost',
              (event) => {
                event.preventDefault()
                setContextLost(true)
              },
              { once: true },
            )
          }}
        >
          <Suspense fallback={null}>
            <Scene
              story={story}
              mode={mode}
              interactive={interactive}
              reducedMotion={reducedMotion}
            />
          </Suspense>
        </Canvas>
      </div>
    </SceneErrorBoundary>
  )
}

useGLTF.preload(MODEL_PATH)
